import React from 'react';
import SearchBar from '../../SearchBar/SearchBar';
import './AssetLoadManagementHeader.scss';
import { SearchTypeEnum } from '../../../types/SearchTypeEnum';
import { useAuthContext } from '../../Auth/AuthProvider';
import { PermissionEnum } from '../../../types/PermissionEnum';
import { useStoreContext } from '../../App/StoreProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown } from 'react-bootstrap';
import { Loading } from '../../_common';

interface SelectedLoad {
    loadId: number | null;
}

export enum SortOrder {
    ASCENDING = 'ASCENDING',
    DESCENDING = 'DESCENDING',
}

interface LoadManagementHeaderProps {
    onCheckAll: (checkedStatus: boolean) => void;
    selectedLoads: SelectedLoad[];
    openSelectedLoads: () => void;
    deleteSelectedLoads: () => void;
    addLoad: () => void;
    isSearchOnLoadId: boolean;
    setIsSearchOnLoadId: React.Dispatch<React.SetStateAction<boolean>>;
    showFilter: boolean;
    setShowFilter: React.Dispatch<React.SetStateAction<boolean>>;
    onDownload: () => void;
    onExportToKronos: () => void;
    onExportToMobDemobForm: () => void;
    addEquipmentModal: () => void;
    loading: boolean;
}

const AssetLoadManagementHeader = ({
    onCheckAll,
    selectedLoads,
    deleteSelectedLoads,
    addLoad,
    addEquipmentModal,
    isSearchOnLoadId,
    setIsSearchOnLoadId,
    showFilter,
    setShowFilter,
    onDownload,
    onExportToKronos,
    onExportToMobDemobForm,
    loading,
}: LoadManagementHeaderProps): React.ReactElement => {
    const isDeleteLoadEnabled = useAuthContext().userHasPermission(PermissionEnum.DeleteAssetLoad);
    const isAddLoadEnabled = useAuthContext().userHasPermission(PermissionEnum.AddAssetLoads);
    const isAddEquipmentsEnabled = useAuthContext().userHasPermission(PermissionEnum.AdminEquipmentUI);
    const [state, dispatch] = useStoreContext();
    const {
        assetLoadManagementList: { filters, searchPhrase },
    } = state;

    return (
        <div className="Header-Container">
            <div
                className="Filter-HeaderIconContainer JobLoads-Filter"
                onClick={() => setShowFilter(!showFilter)}
                style={{ position: 'relative' }}
            >
                <i className="material-icons">filter_list</i>
                {((filters.statuses && filters.statuses.length > 0) ||
                    (filters.trailerTypeIds && filters.trailerTypeIds.length > 0) ||
                    (filters.regionIds && filters.regionIds.length > 0) ||
                    (filters.equipmentTypeIds && filters.equipmentTypeIds.length > 0) ||
                    (filters.fromDate && filters.fromDate.length > 0) ||
                    (filters.toDate && filters.toDate.length > 0) ||
                    (filters.jobIds && filters.jobIds.length > 0) ||
                    (filters.loadTypeIds && filters.loadTypeIds.length > 0) ||
                    (filters.driverIds && filters.driverIds.length > 0) ||
                    (filters.carrierIds && filters.carrierIds.length > 0)) && (
                        <FontAwesomeIcon
                            icon={['fas', 'check-circle']}
                            color="#fb6e3a"
                            size="sm"
                            style={{ position: 'absolute', top: '10px', right: '-4px' }}
                        />
                    )}
            </div>

            {/* For Laptop - Search Bar*/}
            <div className="AssetLoadManagementHeader-Search-Container">
                <div className="AssetLoadManagementHeader-Search-Filters-Container">
                    <div className="AssetLoadManagementHeader-Search-Filter">
                        <input
                            type="radio"
                            id="loadId"
                            name="isLoadId"
                            checked={isSearchOnLoadId}
                            onChange={() => setIsSearchOnLoadId(true)}
                        />
                        <label htmlFor="loadId">Load Id</label>
                    </div>
                    <div className="AssetLoadManagementHeader-Search-Filter AssetLoadManagementHeader-Search-Filter-SearchAll">
                        <input
                            type="radio"
                            id="searchAll"
                            name="isLoadId"
                            checked={!isSearchOnLoadId}
                            onChange={() => setIsSearchOnLoadId(false)}
                        />
                        <label htmlFor="searchAll">Search All</label>
                    </div>
                </div>

                <div className="Header-SearchBar">
                    <SearchBar type={SearchTypeEnum.AssetLoadManagement}></SearchBar>
                </div>
            </div>

            {/* For Tab - Search Bar*/}
            <div className="AssetLoadManagementHeader-Search-Container-For-Tab">
                <div className="AssetLoadManagementHeader-Search-Filters-Container">
                    <div className="AssetLoadManagementHeader-Search-Filter">
                        <input
                            type="radio"
                            id="loadId"
                            name="isLoadId1"
                            checked={isSearchOnLoadId}
                            onChange={() => setIsSearchOnLoadId(true)}
                        />
                        <label htmlFor="loadId">Load Id</label>
                    </div>
                    <div className="AssetLoadManagementHeader-Search-Filter AssetLoadManagementHeader-Search-Filter-SearchAll">
                        <input
                            type="radio"
                            id="searchAll"
                            name="isLoadId1"
                            checked={!isSearchOnLoadId}
                            onChange={() => setIsSearchOnLoadId(false)}
                        />
                        <label htmlFor="searchAll">Search All</label>
                    </div>
                </div>

                <div className="Header-SearchBar">
                    <SearchBar type={SearchTypeEnum.AssetLoadManagement}></SearchBar>
                </div>
            </div>

            {/* For Laptop - Actions*/}
            <div className="AssetLoadManagementHeader-ListHeader">
                <div className="Header-LeftOptions">
                    <input
                        type="checkbox"
                        className="Header-Checkbox"
                        onChange={(event): void => onCheckAll(event.currentTarget.checked)}
                    />
                    <div className="Row">
                        <span className="TableListHeader-Link">{`Select All Created/ Ready for Dispatch (${selectedLoads.length} selected)`}</span>
                    </div>

                    <span className="Header-Divider">|</span>

                    <div
                        className={`Row Header-GroupLink ${isDeleteLoadEnabled ? '' : 'AssetLoadManagementHeader-DeleteButton-Disabled'
                            }`}
                        onClick={deleteSelectedLoads}
                    >
                        <i className="material-icons Header-Icon">delete</i>
                        <span className="TableListHeader-Link">Delete Load</span>
                    </div>

                    <span className="Header-Divider">|</span>

                    <div
                        className={`Row Header-GroupLink ${isAddLoadEnabled ? '' : 'AssetLoadManagementHeader-DeleteButton-Disabled'
                            }`}
                        onClick={addLoad}
                    >
                        <i className="material-icons Header-Icon">add_circle</i>
                        <span className="TableListHeader-Link">Add Load</span>
                    </div>
                </div>
            </div>

            {/* For Tab - Actions*/}
            <div className="AssetLoadManagementHeader-ListHeader-For-Tab">
                <div className="AssetLoadManagementHeader-LeftOptions">
                    <input
                        type="checkbox"
                        className="Header-Checkbox"
                        onChange={(event): void => onCheckAll(event.currentTarget.checked)}
                    />
                    <div className="Row">
                        <span className="TableListHeader-Link">{`Select All Created/ Ready for Dispatch (${selectedLoads.length} selected)`}</span>
                    </div>
                </div>

                <div className="AssetLoadManagementHeader-RightOptions">
                    <div
                        className={`Row Header-GroupLink ${isDeleteLoadEnabled ? '' : 'AssetLoadManagementHeader-DeleteButton-Disabled'
                            }`}
                        onClick={deleteSelectedLoads}
                    >
                        <i className="material-icons Header-Icon">delete</i>
                        <span className="TableListHeader-Link">Delete Load</span>
                    </div>

                    <span className="Header-Divider">|</span>

                    <div
                        className={`Row Header-GroupLink ${isAddLoadEnabled ? '' : 'AssetLoadManagementHeader-DeleteButton-Disabled'
                            }`}
                        onClick={addLoad}
                    >
                        <i className="material-icons Header-Icon">add_circle</i>
                        <span className="TableListHeader-Link">Add Load</span>
                    </div>
                </div>
            </div>

            {/* Actions Dropdown */}
            <div className="AssetLoadManagementHeader-Action-Container">
                <Dropdown className="JobProfile-CenterToggleButton-Container">
                    <Dropdown.Toggle
                        variant="link"
                        id="dropdown-basic"
                        className="JobProfile-CenterToggleButton JobProfile-CenterToggleButton-Container common-Actions"
                    >
                        {loading && <Loading />}
                        <span>Actions</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item>
                            <button className="StageSheet-Dropdown-Button" onClick={onDownload}>
                                <i className="material-icons StageSheet-Icons">print</i>
                                Export Data
                            </button>
                        </Dropdown.Item>
                        <Dropdown.Item>
                            <button className="StageSheet-Dropdown-Button" onClick={onExportToKronos}>
                                <i className="material-icons StageSheet-Icons">print</i>
                                Export to Kronos
                            </button>
                        </Dropdown.Item>
                        <Dropdown.Item>
                            <button className="StageSheet-Dropdown-Button" onClick={onExportToMobDemobForm}>
                                <i className="material-icons StageSheet-Icons">print</i>
                                Export Mob/Demob Form
                            </button>
                        </Dropdown.Item>
                        {
                            isAddEquipmentsEnabled && <Dropdown.Item>
                                <button className="StageSheet-Dropdown-Button" disabled={!isAddEquipmentsEnabled} onClick={addEquipmentModal}>
                                    <i className="material-icons StageSheet-Icons AddEquipment-Icon">add_location_alt</i>
                                    Add Equipment
                                </button>
                            </Dropdown.Item>
                        }
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        </div>
    );
};

export default AssetLoadManagementHeader;

